/**
 * @fileoverview Client-side image compression for invoice uploads.
 * @module shared/utils/compressImage
 */

const MAX_DIMENSION = 2000
const QUALITY = 0.85
const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/jpg'])

/**
 * Resize and re-encode JPG/PNG images to keep upload sizes and LLM token costs low.
 *
 * Resizes the image so its longest side is at most 2000 px and re-encodes
 * to JPEG at quality 0.85. PDFs, unknown types, and any processing failure
 * return the original file unchanged.
 *
 * @param file - The user-selected file.
 * @returns The compressed JPEG, or the original file when compression is not applicable.
 * @example
 * ```ts
 * const compact = await compressImage(rawFile)
 * formData.append('file', compact)
 * ```
 */
export async function compressImage(file: File): Promise<File> {
  if (!IMAGE_TYPES.has(file.type)) return file

  try {
    const bitmap = await createImageBitmap(file)
    const { width, height } = bitmap

    const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height))
    const targetW = Math.round(width * scale)
    const targetH = Math.round(height * scale)

    const canvas = document.createElement('canvas')
    canvas.width = targetW
    canvas.height = targetH
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    ctx.drawImage(bitmap, 0, 0, targetW, targetH)
    bitmap.close?.()

    const blob = await new Promise<Blob | null>(resolve => {
      canvas.toBlob(b => resolve(b), 'image/jpeg', QUALITY)
    })
    if (!blob) return file

    const newName = file.name.replace(/\.(png|jpe?g)$/i, '.jpg')
    return new File([blob], newName, { type: 'image/jpeg', lastModified: file.lastModified })
  } catch {
    return file
  }
}
